import capitalizeFirstLetter from '@/helpers/processing/capitalizeFirstLetter'
import isMobile from '@/helpers/base/isMobile'
import getFormattedDate from '@/helpers/date/getFormattedDate'
import nFormatter from '@/helpers/processing/nFormatter'

// На мобильных тултип фиксированной ширины (экран минус поля) с фиксированными
// колонками, иначе при драге по месяцам он меняет ширину от цифр и «прыгает»
const MOBILE_MARGIN = 10
const MOBILE_COLUMNS = ['36%', '19%', '22.5%', '22.5%']

export default function buildTooltip(context, $t, $tc) {
  const { chartState } = window
  const tooltipModel = context.tooltip

  let tooltipEl = document.getElementById('chartjs-tooltip')
  if (!tooltipEl) {
    tooltipEl = document.createElement('div')
    tooltipEl.id = 'chartjs-tooltip'
    tooltipEl.innerHTML = `<table
      style="background: var(--bg-surface); color: var(--text-primary); padding: 16px; border-collapse: separate; border: 1px solid var(--border-color-2); border-radius: 7px;">
      </table>`
    document.body.appendChild(tooltipEl)
  }
  if (tooltipModel.opacity === 0) {
    tooltipEl.style.opacity = 0
    return
  }

  const mobile = isMobile()
  if (mobile) {
    tooltipEl.style.width = `${window.innerWidth - 2 * MOBILE_MARGIN}px`
    const table = tooltipEl.querySelector('table')
    table.style.width = '100%'
    table.style.tableLayout = 'fixed'
  }

  tooltipEl.classList.remove('above', 'below', 'no-transform')
  if (tooltipModel.yAlign) {
    tooltipEl.classList.add(tooltipModel.yAlign)
  } else {
    tooltipEl.classList.add('no-transform')
  }

  if (tooltipModel.body) {
    getTooltipBody(
      tooltipModel,
      tooltipEl,
      chartState,
      this._chart.data,
      $t,
      $tc,
      mobile
    )
  }

  // POSITION TOOLTIP
  const position = this._chart.canvas.getBoundingClientRect()
  tooltipEl.style.opacity = 1
  tooltipEl.style.position = 'absolute'
  tooltipEl.style.zIndex = 101
  const isShift = this._chart.width / 2 > tooltipModel.caretX
  const shift = isShift ? 0 : -tooltipEl.offsetWidth
  const left = mobile
    ? (window.innerWidth - tooltipEl.offsetWidth) / 2
    : position.left + window.pageXOffset + tooltipModel.caretX + shift
  tooltipEl.style.left = `${left}px`

  tooltipEl.style.top = `${getTooltipTop(
    this._chart,
    tooltipModel,
    tooltipEl,
    position
  )}px`
  tooltipEl.style.font = {
    family: tooltipModel._bodyFontFamily,
    size: `${tooltipModel.bodyFontSize}px`,
    style: tooltipModel._bodyFontStyle,
  }
  tooltipEl.style.pointerEvents = 'none'
}

function getTooltipTop(chart, tooltipModel, tooltipEl, position) {
  const pageTop = position.top + window.pageYOffset
  const { chartArea } = chart

  // На мобильных тултип не должен закрывать ни линии, ни палец при драге:
  // ставим его под областью графика (над подписями оси X), если снизу
  // не помещается в экран — над графиком, иначе прижимаем к низу экрана
  if (isMobile() && chartArea) {
    const height = tooltipEl.offsetHeight
    const belowTop = position.top + chartArea.bottom
    const aboveTop = position.top + chartArea.top - height
    let top
    if (belowTop + height <= window.innerHeight) top = belowTop
    else if (aboveTop >= 0) top = aboveTop
    else top = Math.max(0, window.innerHeight - height)
    return top + window.pageYOffset
  }

  const fromTooltipToBottom =
    window.innerHeight - (position.top + tooltipModel.caretY)
  const shiftTop =
    fromTooltipToBottom < tooltipEl.offsetHeight
      ? fromTooltipToBottom - tooltipEl.offsetHeight - 25
      : 0
  return pageTop + tooltipModel.caretY + shiftTop
}

function getTooltipBody(
  tooltipModel,
  tooltipEl,
  chartState,
  data,
  $t,
  $tc,
  mobile
) {
  // const isChangePopover = false // chartState.state.firstClickedIndex && chartState.state.secondClickedIndex

  const titleLines = tooltipModel.title || []
  const bodyFiltered = tooltipModel.body.filter(item => {
    if (item.lines[0].search(':') > -1) return true
    return false
  })
  const bodyLines = bodyFiltered.map(item => item.lines)
  // отступ колонок с процентами: на мобильном компактнее, чтобы влезли в фиксированную ширину
  const growthPadding = mobile ? 8 : 20
  let innerHtml = ''
  if (mobile) {
    innerHtml += `<colgroup>${MOBILE_COLUMNS.map(
      width => `<col style="width: ${width}">`
    ).join('')}</colgroup>`
  }
  titleLines.forEach(title => {
    const titleFinal = getFormattedDate(title)
    innerHtml += `<thead>
      <tr style="font-weight: bold; font-size: 17px; margin-bottom: 9px; margin-right: -200px">
        <th scope="col" colspan="4" style="white-space: nowrap;">${capitalizeFirstLetter(
          titleFinal
        )}</th>
      </tr>
    </thead>`
  })
  innerHtml += `
  <tbody>
    <tr style="font-weight: 400; color: var(--text-secondary3); text-transform: uppercase; font-size: 12px;">
      <td style="font-weight: 400; padding-right: 30px;">${$t(
        'main.traffic.popupTitle'
      )}</td>
      <td class="text-end" style="padding: 0px;">${$t(
        'main.traffic.popupValue'
      )}</td>`

  const hoveredIndex = tooltipModel.dataPoints[0].dataIndex
  const prevMonthIndex = hoveredIndex - 1
  const month = prevMonthIndex >= 0
    ? getFormattedDate(data.labels[prevMonthIndex], { year: null })
    : '-'
  innerHtml += `<td class="text-end" style="padding-left: ${growthPadding}px; white-space: nowrap;">${month}</td>`
  innerHtml += `<td class="text-end" style="padding-left: ${growthPadding}px; white-space: nowrap;">${$tc(
    'helpers.date.year',
    1
  )}</td>`
  innerHtml += `</tr>`

  bodyLines.forEach((body, i) => {
    const colors = tooltipModel.labelColors[i]
    innerHtml += '<tr>'
    const label = body[0].split(':')
    const labelName = label[0]
    // const labelValue = new Intl.NumberFormat().format(label[1])

    const labelValue = +label[1]?.replace(/[\s,]+/g, '')
    innerHtml += `<td style="font-size: 14px;color: var(--text-primary);padding-right: ${
      mobile ? 8 : 30
    }px; white-space:nowrap; overflow: hidden; text-overflow: ellipsis;">
      <span style="display: inline-block; width:12px; height:12px; border-radius: 4px; margin-right: 4px; background:${colors.borderColor};"></span>
      ${labelName}
    </td>`

    innerHtml += `<td align="right" style="color: var(--text-primary);font-weight: 500;white-space:nowrap;padding: 0px; font-variant-numeric: tabular-nums;">
      ${nFormatter(labelValue)}</td>`
    const growthOptions = { data, label: labelName, index: hoveredIndex, padding: growthPadding }
    innerHtml += getHTMLGrowth({ ...growthOptions, period: 1 })
    innerHtml += getHTMLGrowth({ ...growthOptions, period: 12 })
    innerHtml += '</tr>'
  })
  innerHtml += '</tbody>'

  const tableRoot = tooltipEl.querySelector('table')
  tableRoot.innerHTML = innerHtml
}

function getHTMLGrowth(options) {
  const dataset = options.data.datasets.filter(
    item => item.label === options.label
  )

  const len = options.index
  const max = dataset[0].data[len]
  const min = dataset[0].data[len - options.period]

  const growth = getGrowth(max, min)

  let growthOptions
  if (growth > 0)
    growthOptions = { color: 'var(--success-color)', symbol: '+', last: '%' }
  else if (growth <= 0)
    growthOptions = { color: 'var(--danger-color)', symbol: '', last: '%' }
  else growthOptions = { color: 'var(--text-secondary3)', symbol: '', last: '' }

  return `<td align="right" style="padding-left: ${options.padding}px; white-space: nowrap; font-variant-numeric: tabular-nums; color:
    ${growthOptions.color}">${growthOptions.symbol}${growth}${growthOptions.last}
  </td>`
}

function getGrowth(max, min) {
  if (!max || !min) return '-'
  const diff = max - min
  const growth = (diff / min) * 100
  if (growth > 99999 || growth < -99999) return '-'
  return growth ? ((diff / min) * 100).toFixed(1) : '-'
}
